import React, { useEffect, useState, useRef } from 'react';
import './FavoritesSection.css';
import { allVideos, getVideoUrl } from '../utils/localVideos';

interface VideoItem {
  name: string;
  video: string; // Video URL
  category: string;
}

interface FavoriteCardProps {
  video: VideoItem;
  onVideoClick: () => void;
}

const FavoriteCard: React.FC<FavoriteCardProps> = ({ video, onVideoClick }) => {
  const [showVideo, setShowVideo] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoUrl = video.video;
  // Disable thumbnail generation to prevent crashes - use placeholder
  const thumbnailSrc = null;

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl || !videoUrl) return;
    
    let cleanup: (() => void) | null = null;
    
    if (showVideo) {
      // Set src if not already set
      if (!videoEl.src || videoEl.src !== videoUrl) {
        videoEl.src = videoUrl;
        videoEl.load();
      }
      
      // Play when ready
      const tryPlay = () => {
        if (videoEl.readyState >= 2 && showVideo) {
          videoEl.currentTime = 0;
          videoEl.play().catch(() => {});
        }
      };
      
      if (videoEl.readyState >= 2) {
        tryPlay();
      } else {
        const onCanPlay = () => {
          if (showVideo) {
            tryPlay();
          }
        };
        videoEl.addEventListener('canplay', onCanPlay, { once: true });
        cleanup = () => {
          videoEl.removeEventListener('canplay', onCanPlay);
        };
      }
    } else {
      videoEl.pause();
      videoEl.currentTime = 0;
    }
    
    return () => {
      if (cleanup) cleanup();
    };
  }, [showVideo, videoUrl]);

  const handleMouseEnter = () => {
    setShowVideo(true);
  };

  const handleMouseLeave = () => {
    setShowVideo(false);
  };

  return (
    <div 
      className="favorite-card"
      onClick={onVideoClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="favorite-video-container">
        {!showVideo && (
          thumbnailSrc ? (
            <img
              src={thumbnailSrc}
              alt={video.name}
              className="favorite-thumbnail"
            />
          ) : (
            <div className="favorite-thumbnail favorite-thumbnail-placeholder" style={{ 
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              color: '#999'
            }}>
              <span>Loading...</span>
            </div>
          )
        )}
        {showVideo && (
          <video
            ref={videoRef}
            src={videoUrl}
            poster={thumbnailSrc || undefined}
            className="favorite-video-element"
            muted
            playsInline
            loop
            preload="none"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              position: 'absolute',
              top: '0',
              left: '0'
            }}
            onError={(e) => {
              console.error('Favorite video load error:', videoUrl, e);
            }}
          >
            <source src={videoUrl} type="video/mp4" />
          </video>
        )}
        <div className="favorite-overlay">
          <span className="favorite-badge">❤️ FAVORITE</span>
        </div>
      </div>
      <h3 className="favorite-title">{video.name}</h3>
      <span className="favorite-category">{video.category}</span>
    </div>
  );
};

interface FavoritesSectionProps {
  onVideoClick: () => void;
  onNavigate?: (page: string) => void;
}

const FavoritesSection: React.FC<FavoritesSectionProps> = ({ onVideoClick, onNavigate }) => {
  const [favoriteVideos, setFavoriteVideos] = useState<VideoItem[]>([]);

  useEffect(() => {
    const loadFavorites = () => {
      const stored = localStorage.getItem('snapflix_favorites');
      if (stored) {
        try {
          const favoriteNames = new Set(JSON.parse(stored));
          
          // Convert allVideos to VideoItem format and filter by favorite names
          const videosList: VideoItem[] = allVideos
            .filter(v => favoriteNames.has(v.name))
            .map(v => ({
              name: v.name,
              video: getVideoUrl(v.videoPath),
              category: v.category || 'All'
            }));
          
          // Remove duplicates - if same video appears in multiple categories, keep first occurrence
          const uniqueVideos = videosList.reduce((acc, current) => {
            const existing = acc.find(v => v.name === current.name && v.video === current.video);
            if (!existing) {
              acc.push(current);
            }
            return acc;
          }, [] as VideoItem[]);
          
          setFavoriteVideos(uniqueVideos);
        } catch (error) {
          console.error('Error loading favorites:', error);
          setFavoriteVideos([]);
        }
      }
    };
    
    loadFavorites();
    
    // Listen for storage changes (when favorites are updated)
    const handleStorageChange = () => {
      loadFavorites();
    };
    
    // Use custom event for same-window updates
    const handleCustomStorageChange = () => {
      loadFavorites();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', loadFavorites);
    window.addEventListener('favoritesUpdated', handleCustomStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', loadFavorites);
      window.removeEventListener('favoritesUpdated', handleCustomStorageChange);
    };
  }, []);
  
  if (favoriteVideos.length === 0) {
    return null;
  }
  
  return (
    <div className="favorites-section">
      <div className="favorites-header">
        <h2 className="favorites-title">
          ❤️ Your Favorite Videos
        </h2>
        {onNavigate && (
          <button 
            className="view-all-btn"
            onClick={() => onNavigate('videos')}
          >
            View All →
          </button>
        )}
      </div>
      
      <div className="favorites-grid">
        {favoriteVideos.slice(0, 4).map((video) => (
          <FavoriteCard key={`${video.name}-${video.video}`} video={video} onVideoClick={onVideoClick} />
        ))}
      </div>
    </div>
  );
};

export default FavoritesSection;

